/**
 * 3D 导演台工程模型（P0-1d）
 *
 * 边界（蓝图 D3 / §2 守则 1）：导演台**只出构图参考图，不做真实渲染**。
 * 因此这里没有材质、灯光、PBR、路径动画等字段 —— 加进来就是给自己挖无底洞。
 *
 * 与竞品的一处改进（蓝图 §7-1）：站位模板可重复打开与局部套用，
 * 不像竞品那样"应用一次后编辑器内再无入口"。
 */

import type { CharacterRig } from './pose';

export type Vec3 = readonly [x: number, y: number, z: number];

export interface Transform {
  position: Vec3;
  /** 欧拉角，单位角度（UI 与存储都用角度，渲染时再转弧度，避免两套单位混淆） */
  rotation: Vec3;
  scale: Vec3;
}

/** 素体类型。对齐竞品 6 种；均要求带手指骨骼（蓝图硬约束） */
export type BodyType = 'standard' | 'robust' | 'broad' | 'child' | 'crowd' | 'geometry';

export type GeometryType = 'box' | 'sphere' | 'cylinder' | 'torus' | 'cone' | 'pyramid';

export interface DirectorObject {
  /** 唯一键；name 仅展示（竞品模板出现过重名对象） */
  id: string;
  kind: 'character' | 'prop';
  name: string;
  bodyType: BodyType | null;
  /** 几何体形状，仅 bodyType='geometry' 时有值 */
  geometryType?: GeometryType | undefined;
  transform: Transform;
  /** 统一缩放倍数，范围 0.2–3 */
  uniformScale: number;
  /** 三轴独立缩放是否锁定（默认锁定，与竞品一致） */
  scaleAxisLocked: boolean;
  visible: boolean;
  locked: boolean;
  /** 用于视口里区分角色的标识色 */
  tint: string;
  rig?: CharacterRig | undefined;
}

export type CameraTargetMode = 'point' | 'follow';

export interface CameraTarget {
  /** follow 时绑定角色 id，镜头自动跟随 */
  mode: CameraTargetMode;
  point: Vec3;
  followId: string | null;
}

export interface DirectorCamera {
  id: string;
  name: string;
  position: Vec3;
  /**
   * 唯一镜头参数，10–120。
   * 刻意不给 rotation：朝向由 target 反解（竞品验证过的极简设计）。
   * 也不给焦距/光圈/快门/ISO/景深：那些属于真实渲染，超出本项目边界。
   */
  fov: number;
  target: CameraTarget;
}

export interface GroundConfig {
  visible: boolean;
  /** 0–1 */
  opacity: number;
  /** -5–5 */
  height: number;
}

export interface PanoramaConfig {
  enabled: boolean;
  /** 10–300 */
  radius: number;
  /** -180–180，水平旋转 */
  yawDeg: number;
  /**
   * 全景图来源：指向同一画布上的图片节点 id（蓝图 D2「连线代替上传按钮」）。
   * 导演台内部不提供任何上传入口。
   */
  sourceNodeId: string | null;
}

export interface SceneConfig {
  /** 0.1–3 */
  scale: number;
  /** 30–150，视距渐隐 */
  fadeEnd: number;
  offset: Vec3;
  rotation: Vec3;
  skyColor: string;
  gridColor: string;
  groundColor: string;
  ground: GroundConfig;
  gridSnap: boolean;
  showLabels: boolean;
  panorama: PanoramaConfig;
}

export interface DirectorCapture {
  id: string;
  name: string;
  cameraId: string;
  createdAt: string;
  /** 已落成画布节点时为节点 id，否则 null */
  pushedNodeId: string | null;
}

export interface DirectorProject {
  schemaVersion: number;
  scene: SceneConfig;
  objects: DirectorObject[];
  cameras: DirectorCamera[];
  activeCameraId: string | null;
  captures: DirectorCapture[];
}

export const DIRECTOR_SCHEMA_VERSION = 1;

export const FOV_MIN = 10;
export const FOV_MAX = 120;
export const UNIFORM_SCALE_MIN = 0.2;
export const UNIFORM_SCALE_MAX = 3;
export const SCENE_SCALE_MIN = 0.1;
export const SCENE_SCALE_MAX = 3;
export const FADE_MIN = 30;
export const FADE_MAX = 150;
export const PANORAMA_RADIUS_MIN = 10;
export const PANORAMA_RADIUS_MAX = 300;
export const GROUND_OPACITY_MIN = 0;
export const GROUND_OPACITY_MAX = 1;
export const GROUND_HEIGHT_MIN = -5;
export const GROUND_HEIGHT_MAX = 5;

/** 视口标识色板（我方自定义，不复用竞品色值） */
export const TINT_PALETTE: readonly string[] = [
  '#E8A33D', '#6FB3E8', '#4CC38A', '#C79BFF', '#FF7B9C',
  '#5FD0E0', '#B5D34A', '#F2A93D', '#8FA3BF', '#EF6A5E',
  '#7B9EFF', '#4FC3A1', '#D9A87C', '#9AA6BC',
];

/** 选中高亮框颜色（模型层定义、UI 层对齐 accent-hover token；hex 只允许出现在这里） */
export const SELECTION_COLOR = '#F5C97A';

/** 视口机位图标配色（对齐 UI token：ink-secondary / state-info / accent） */
export const CAMERA_GIZMO_COLOR = '#9AA6BC';
export const CAMERA_GIZMO_SELECTED_COLOR = '#6FB3E8';
export const CAMERA_GIZMO_ACTIVE_COLOR = '#E8A33D';

/** 按序号取色板色（兜底逻辑集中在模型层，组件里就不该出现 hex 字面量） */
export function tintAt(index: number): string {
  const i = ((index % TINT_PALETTE.length) + TINT_PALETTE.length) % TINT_PALETTE.length;
  return TINT_PALETTE[i] as string;
}

/** 网格吸附步长（米），scene.gridSnap 开启时 gizmo 平移按它对齐 */
export const GRID_SNAP_STEP_M = 0.5;

const ZERO3: Vec3 = [0, 0, 0];

export function defaultScene(): SceneConfig {
  return {
    scale: 1,
    fadeEnd: 70,
    offset: ZERO3,
    rotation: ZERO3,
    skyColor: '#0B0B0D',
    gridColor: '#2E2B27',
    groundColor: '#1B1A18',
    ground: { visible: true, opacity: 0.4, height: 0 },
    gridSnap: false,
    showLabels: true,
    panorama: { enabled: true, radius: 60, yawDeg: 0, sourceNodeId: null },
  };
}

export function createEmptyDirectorProject(): DirectorProject {
  return {
    schemaVersion: DIRECTOR_SCHEMA_VERSION,
    scene: defaultScene(),
    objects: [],
    cameras: [],
    activeCameraId: null,
    captures: [],
  };
}

export function clampFov(v: number): number {
  if (!Number.isFinite(v)) {
    return FOV_MIN;
  }
  return Math.min(FOV_MAX, Math.max(FOV_MIN, v));
}

export function clampUniformScale(v: number): number {
  if (!Number.isFinite(v)) {
    return 1;
  }
  return Math.min(UNIFORM_SCALE_MAX, Math.max(UNIFORM_SCALE_MIN, v));
}

export function findCamera(p: DirectorProject, id: string): DirectorCamera | undefined {
  return p.cameras.find((c) => c.id === id);
}

export function activeCamera(p: DirectorProject): DirectorCamera | null {
  if (p.activeCameraId === null) {
    return null;
  }
  return findCamera(p, p.activeCameraId) ?? null;
}

export function findObject(p: DirectorProject, id: string): DirectorObject | undefined {
  return p.objects.find((o) => o.id === id);
}

/** 角色头高（米），用于按实际身高算 target.y —— 竞品把 target.y 写死，跪姿/病床需人工改 */
export const HEAD_HEIGHT_M = 1.65;

/**
 * 计算某机位注视点的推荐高度：绑定角色时取该角色实际头高，否则用默认值。
 * 这是蓝图 §7-6「target.y 按角色头高实算」的落点，避免跪姿镜头注视点在人身中部。
 */
export function suggestTargetY(p: DirectorProject, camera: DirectorCamera): number {
  if (camera.target.mode === 'follow' && camera.target.followId !== null) {
    const obj = findObject(p, camera.target.followId);
    if (obj !== undefined) {
      return HEAD_HEIGHT_M * obj.uniformScale * obj.transform.scale[1];
    }
  }
  return 1.05;
}

/** 结构校验：返回问题列表，空即合法 */
export function validateDirectorProject(doc: unknown): string[] {
  const problems: string[] = [];
  if (typeof doc !== 'object' || doc === null) {
    return ['导演台工程不是合法对象'];
  }
  const d = doc as Partial<DirectorProject>;

  if (!Array.isArray(d.objects)) {
    problems.push('objects 不是数组');
  }
  if (!Array.isArray(d.cameras)) {
    problems.push('cameras 不是数组');
  }
  if (typeof d.scene !== 'object' || d.scene === null) {
    problems.push('缺少 scene 配置');
  }

  const ids = new Set<string>();
  (d.objects ?? []).forEach((o, i) => {
    if (typeof o?.id !== 'string' || o.id === '') {
      problems.push(`第 ${i} 个对象缺少 id`);
      return;
    }
    if (ids.has(o.id)) {
      problems.push(`对象 id 重复：${o.id}`);
    }
    ids.add(o.id);
    if (o.uniformScale !== undefined && (o.uniformScale < UNIFORM_SCALE_MIN || o.uniformScale > UNIFORM_SCALE_MAX)) {
      problems.push(`对象 ${o.id} 的 uniformScale 超出 ${UNIFORM_SCALE_MIN}-${UNIFORM_SCALE_MAX}`);
    }
  });

  const camIds = new Set<string>();
  (d.cameras ?? []).forEach((c, i) => {
    if (typeof c?.id !== 'string' || c.id === '') {
      problems.push(`第 ${i} 个机位缺少 id`);
      return;
    }
    if (camIds.has(c.id)) {
      problems.push(`机位 id 重复：${c.id}`);
    }
    camIds.add(c.id);
    if (typeof c.fov === 'number' && (c.fov < FOV_MIN || c.fov > FOV_MAX)) {
      problems.push(`机位 ${c.id} 的 fov 超出 ${FOV_MIN}-${FOV_MAX}`);
    }
    if (c.target?.mode === 'follow' && typeof c.target.followId === 'string' && !ids.has(c.target.followId)) {
      problems.push(`机位 ${c.id} 绑定的角色 ${c.target.followId} 不存在`);
    }
  });

  if (typeof d.activeCameraId === 'string' && !camIds.has(d.activeCameraId)) {
    problems.push(`activeCameraId ${d.activeCameraId} 不存在`);
  }

  return problems;
}
